<script setup lang="ts">
import type { models } from 'cesg-shared';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const emit = defineEmits<{
  (e: 'file-loaded', file: models.SupplierInvoiceFile): void;
}>();

const { t } = useI18n();

const isDragging = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const processFile = async (file: File) => {
  if (file.type !== 'application/pdf') {
    alert(t('invoiceUploader.onlyPdfAllowed'));
    return;
  }

  try {
    const base64 = await fileToBase64(file);

    const invoiceFile: models.SupplierInvoiceFile = {
      fileId: Date.now().toString(),
      name: file.name,
      blob: base64,
    };

    emit('file-loaded', invoiceFile);
  } catch (error) {
    console.error(t('invoiceUploader.errorDuringFileRead'), error);
  }
};

const onDrop = (e: DragEvent) => {
  isDragging.value = false;
  const file = e.dataTransfer?.files[0];
  if (file) processFile(file);
};

const onFileSelect = (e: Event) => {
  const target = e.target as HTMLInputElement;
  if (target.files?.[0]) processFile(target.files[0]);
};
</script>

<template>
  <div
    class="uploader-container"
    :class="{ dragging: isDragging }"
    @dragover.prevent="isDragging = true"
    @dragleave.prevent="isDragging = false"
    @drop.prevent="onDrop"
    @click="fileInput?.click()"
  >
    <input
      type="file"
      ref="fileInput"
      @change="onFileSelect"
      accept=".pdf,image/*"
      hidden
    />

    <div class="content">
      <div class="icon">📄</div>
      <p>
        <strong>{{ t('invoiceUploader.dropAndRelease') }}</strong>
        {{ t('invoiceUploader.invoiceHere') }}
      </p>
      <p class="sub-text">{{ t('invoiceUploader.clickToChooseFile') }}</p>
    </div>
  </div>
</template>

<style scoped>
.uploader-container {
  width: 100%;
  height: fit-content;
  min-height: 800px;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #f8fafc;
  transition: all 0.2s;
}

.uploader-container:hover {
  border-color: #94a3b8;
  background-color: #f1f5f9;
}

.uploader-container.dragging {
  border-color: #3b82f6;
  background-color: #eff6ff;
  transform: scale(0.99);
}

.content {
  text-align: center;
  color: #64748b;
}
.icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}
.sub-text {
  font-size: 0.85rem;
  margin-top: 0.5rem;
}
</style>
